import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { RotateCcw, TriangleAlert } from 'lucide-react';
import {
  BoutonMint,
  BoutonPrincipal,
  BoutonSecondaire,
  Chargement,
  Compteur,
  EnteteEcran,
  MessageErreur,
  Pagination,
  Pastille,
  PastilleSite,
} from '../components/ui/Primitives';
import { BarreSites } from '../components/ui/BarreSites';
import { ChampFiltre, ChampRecherche, Onglets, PanneauOnglet } from '../components/ui/Onglets';
import { Modale } from '../components/ui/Modale';
import { useToast } from '../components/ui/useToast';
import { ListeChangements } from '../components/modifications-sensibles/ElementsDemande';
import { usePerimetre } from '../site/usePerimetre';
import { ErreurApi } from '../lib/api';
import { useTaillePage } from '../lib/useTaillePage';
import { horodatageReunion } from '../lib/emargement-affichage';
import {
  MOTIF_MAX,
  MOTIF_MIN,
  useApprouverDemande,
  useModificationsSensibles,
  useRefuserDemande,
  type FamilleModification,
  type VueDemande,
} from '../lib/useModificationsSensibles';

type Vue = 'A_TRAITER' | 'HISTORIQUE';

const VUES: readonly Vue[] = ['A_TRAITER', 'HISTORIQUE'];

const LIBELLES_VUE: Record<Vue, string> = {
  A_TRAITER: 'À traiter',
  HISTORIQUE: 'Historique des décisions',
};

const MESSAGES_VIDE: Record<Vue, string> = {
  A_TRAITER: 'Aucune modification sensible n’attend votre décision dans ce périmètre.',
  HISTORIQUE: 'Aucune décision sur les douze derniers mois dans ce périmètre.',
};

function messageErreur(erreur: unknown, defaut: string): string {
  return erreur instanceof ErreurApi ? erreur.message : defaut;
}

function pluriel(nombre: number, singulier: string, plurielTexte = `${singulier}s`): string {
  return `${nombre} ${nombre > 1 ? plurielTexte : singulier}`;
}

/** Recherche sans casse ni accent : « Begue » trouve « Bègue ». */
function cleRecherche(texte: string): string {
  return texte.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
}

function vueDeLAdresse(valeur: string | null): Vue {
  return VUES.includes(valeur as Vue) ? (valeur as Vue) : 'A_TRAITER';
}

function dansLaVue(ligne: VueDemande, vue: Vue): boolean {
  return vue === 'A_TRAITER' ? ligne.statut === 'EN_ATTENTE' : ligne.statut !== 'EN_ATTENTE';
}

/**
 * Validation des modifications sensibles (phase 5, TF-437) : la direction
 * approuve ce que les pôles demandent — une présence d'une feuille clôturée,
 * la réouverture d'une feuille, une note publiée, l'identité d'un dossier
 * entré en formation — ou le refuse avec un motif, adressé à son auteur. Rien
 * n'est modifié avant sa décision. Ses propres gestes, et les présences que le
 * pôle pédagogique corrige lui-même (TF-476), s'inscrivent au même registre.
 *
 * L'écran ne décide rien : ce qui change, ce qui empêche un geste et les
 * compteurs viennent de `/api/modifications-sensibles`. Une demande ciblée par
 * une notification (`?demande=`) s'affiche seule.
 */
export function DirModificationsPage(): JSX.Element {
  const { site } = usePerimetre();
  const { data, isLoading, error } = useModificationsSensibles(site);
  const [parametres, setParametres] = useSearchParams();
  const vue = vueDeLAdresse(parametres.get('etat'));
  const ciblee = parametres.get('demande');
  const [recherche, setRecherche] = useState('');
  const [famille, setFamille] = useState<FamilleModification | ''>('');
  const [aRefuser, setARefuser] = useState<VueDemande | null>(null);
  const compteurs = data?.compteurs;

  const changerAdresse = (modifier: (prochains: URLSearchParams) => void): void => {
    setParametres(
      (avant) => {
        const prochains = new URLSearchParams(avant);
        modifier(prochains);
        return prochains;
      },
      { replace: true },
    );
  };
  const changerVue = (suivante: Vue): void =>
    changerAdresse((prochains) => {
      prochains.delete('demande');
      if (suivante === 'A_TRAITER') prochains.delete('etat');
      else prochains.set('etat', suivante);
    });

  const demandeCiblee = ciblee ? (data?.lignes.find((ligne) => ligne.id === ciblee) ?? null) : null;
  const lignes = useMemo(() => {
    if (!data) return [];
    if (demandeCiblee) return [demandeCiblee];
    const terme = cleRecherche(recherche);
    return data.lignes.filter((ligne) => {
      if (!dansLaVue(ligne, vue)) return false;
      if (famille && ligne.famille !== famille) return false;
      if (!terme) return true;
      return [
        ligne.objet,
        ligne.apprenant ? `${ligne.apprenant.prenom} ${ligne.apprenant.nom}` : '',
        ligne.demandePar ?? '',
        ligne.motif,
      ].some((champ) => cleRecherche(champ).includes(terme));
    });
  }, [data, vue, recherche, famille, demandeCiblee]);
  const filtreActif = recherche.trim() !== '' || famille !== '';

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre="Modifications sensibles"
        sousTitre="Présences, notes publiées, données administratives · validation de la direction, corrections directes inscrites au registre"
        actions={<BarreSites />}
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Compteur
          libelle="À traiter"
          valeur={compteurs?.aTraiter ?? '—'}
          ton={compteurs && compteurs.aTraiter > 0 ? 'warm' : 'navy'}
          detail={
            compteurs === undefined || compteurs.aTraiter === 0
              ? 'Aucune demande en attente'
              : compteurs.plusAncienneJours === 0
                ? 'La plus ancienne demandée aujourd’hui'
                : `La plus ancienne demandée il y a ${pluriel(compteurs.plusAncienneJours ?? 0, 'jour')}`
          }
        />
        <Compteur
          libelle="Approuvées"
          valeur={compteurs?.approuvees ?? '—'}
          ton="mint"
          detail="sur les 30 derniers jours"
        />
        <Compteur
          libelle="Refusées"
          valeur={compteurs?.refusees ?? '—'}
          ton="blue"
          detail="sur les 30 derniers jours"
        />
        <Compteur
          libelle="Appliquées directement"
          valeur={compteurs?.directes ?? '—'}
          ton="navy"
          detail="direction, et pôle pédagogique pour les présences · 30 jours"
        />
      </div>

      {isLoading && <Chargement libelle="Chargement des demandes…" />}
      {error && <MessageErreur message={messageErreur(error, 'Les demandes n’ont pas pu être chargées.')} />}

      {data && (
        <>
          <Onglets
            etiquette="Modifications sensibles"
            onglets={VUES.map((cle) => ({
              cle,
              libelle: LIBELLES_VUE[cle],
              compte: data.lignes.filter((ligne) => dansLaVue(ligne, cle)).length,
            }))}
            actif={vue}
            onChanger={changerVue}
            idPanneau="panneau-modifications"
            actions={
              <>
                <ChampRecherche
                  valeur={recherche}
                  onChange={setRecherche}
                  etiquette="Rechercher une demande"
                  placeholder="Apprenant, auteur, motif…"
                />
                <ChampFiltre
                  valeur={famille}
                  onChange={(valeur) => setFamille(valeur as FamilleModification | '')}
                  etiquette="Nature"
                >
                  <option value="">Toutes les natures</option>
                  {data.options.familles.map((option) => (
                    <option key={option.valeur} value={option.valeur}>
                      {option.libelle}
                      {vue === 'A_TRAITER' ? ` (${data.compteurs.parFamille[option.valeur]})` : ''}
                    </option>
                  ))}
                </ChampFiltre>
                {filtreActif && (
                  <button
                    type="button"
                    onClick={() => {
                      setRecherche('');
                      setFamille('');
                    }}
                    className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg px-3 text-xs font-semibold text-codialis-sky hover:underline"
                  >
                    <RotateCcw className="h-3.5 w-3.5" aria-hidden />
                    Réinitialiser
                  </button>
                )}
              </>
            }
          />
          {ciblee && (
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-2xl bg-codialis-sand px-4 py-3 text-sm text-codialis-navy">
              <span className="min-w-0">
                {demandeCiblee
                  ? 'Demande ouverte depuis une notification.'
                  : 'Cette demande n’est plus dans ce périmètre ou a plus de douze mois.'}
              </span>
              <BoutonSecondaire onClick={() => changerAdresse((prochains) => prochains.delete('demande'))}>
                Voir toutes les demandes
              </BoutonSecondaire>
            </div>
          )}
          <PanneauOnglet id="panneau-modifications" actif={vue}>
            <ListeDemandes
              lignes={lignes}
              vide={filtreActif ? 'Aucune demande ne correspond aux filtres.' : MESSAGES_VIDE[vue]}
              onRefuser={setARefuser}
            />
          </PanneauOnglet>
        </>
      )}

      {aRefuser && <ModaleRefus demande={aRefuser} onFermer={() => setARefuser(null)} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Liste
// ---------------------------------------------------------------------------

function ListeDemandes({
  lignes,
  vide,
  onRefuser,
}: {
  lignes: VueDemande[];
  vide: string;
  onRefuser: (demande: VueDemande) => void;
}): JSX.Element {
  const [page, setPage] = useState(1);
  const [taillePage, setTaillePage] = useTaillePage();
  useEffect(() => setPage(1), [taillePage, lignes.length]);

  if (lignes.length === 0) {
    return <p className="rounded-2xl bg-white px-4 py-12 text-center text-sm text-codialis-gray shadow-card">{vide}</p>;
  }

  const affichees = lignes.slice((page - 1) * taillePage, page * taillePage);
  return (
    <div className="space-y-4">
      <ul className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        {affichees.map((demande) => (
          <li key={demande.id} className="min-w-0">
            <CarteDemande demande={demande} onRefuser={onRefuser} />
          </li>
        ))}
      </ul>
      <div className="overflow-hidden rounded-2xl bg-white shadow-card">
        <Pagination
          page={page}
          pages={Math.max(1, Math.ceil(lignes.length / taillePage))}
          total={lignes.length}
          affiches={affichees.length}
          taillePage={taillePage}
          onPage={setPage}
          onChangerTaille={setTaillePage}
          uniteLabel="demande"
        />
      </div>
    </div>
  );
}

function CarteDemande({
  demande,
  onRefuser,
}: {
  demande: VueDemande;
  onRefuser: (demande: VueDemande) => void;
}): JSX.Element {
  const toast = useToast();
  const approuver = useApprouverDemande();
  const enAttente = demande.statut === 'EN_ATTENTE';

  return (
    <article className="flex h-full min-w-0 flex-col gap-3 rounded-2xl bg-white p-4 shadow-card sm:p-5">
      <header className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="font-display text-base font-bold text-codialis-navy">{demande.typeLibelle}</h3>
          <p className="break-words text-xs text-codialis-gray">{demande.objet}</p>
        </div>
        <div className="flex flex-wrap items-center gap-1">
          <PastilleSite site={demande.site.code} />
          <Pastille texte={demande.familleLibelle} ton="neutral" />
          <Pastille texte={demande.statutLibelle} ton={demande.statutTon} />
        </div>
      </header>

      {demande.apprenant && (
        <p className="text-sm font-semibold text-codialis-navy">
          {demande.apprenant.prenom} {demande.apprenant.nom}
        </p>
      )}

      <div className="rounded-xl bg-codialis-sand px-3 py-2">
        <ListeChangements changements={demande.changements} />
      </div>

      <div className="space-y-1 text-xs text-codialis-gray">
        <p className="whitespace-pre-line break-words text-codialis-navy">« {demande.motif} »</p>
        <p>
          {demande.directe ? 'Faite' : 'Demandée'} le {horodatageReunion(demande.demandeLe)}
          {demande.demandePar ? ` par ${demande.demandePar}` : ''}
          {demande.attenteJours !== null && demande.attenteJours > 0
            ? ` · en attente depuis ${pluriel(demande.attenteJours, 'jour')}`
            : ''}
        </p>
        {demande.decision && !demande.directe && (
          <p>
            {demande.statut === 'RETIREE' ? 'Retirée' : demande.statut === 'REFUSEE' ? 'Refusée' : 'Approuvée'} le{' '}
            {horodatageReunion(demande.decision.le)}
            {demande.decision.par ? ` par ${demande.decision.par}` : ''}
          </p>
        )}
        {demande.decision?.motifRefus && (
          <p className="rounded-lg border border-red-100 bg-red-50 px-2 py-1.5 text-red-800">
            Motif du refus : « {demande.decision.motifRefus} »
          </p>
        )}
      </div>

      {enAttente && demande.donneeChangee && (
        <p className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <TriangleAlert className="mt-px h-4 w-4 shrink-0" aria-hidden />
          <span className="min-w-0">{demande.refusApprobation}</span>
        </p>
      )}

      <div className="mt-auto flex flex-wrap items-center gap-2 pt-1">
        {demande.refusApprobation === null && (
          <BoutonMint
            disabled={approuver.isPending}
            onClick={() =>
              approuver.mutate(demande.id, {
                onSuccess: (resultat) => toast(resultat.message),
                onError: (refus) => toast(messageErreur(refus, 'La demande n’a pas pu être approuvée.')),
              })
            }
          >
            {approuver.isPending ? 'Application…' : 'Approuver et appliquer'}
          </BoutonMint>
        )}
        {demande.refusRefus === null && <BoutonSecondaire onClick={() => onRefuser(demande)}>Refuser</BoutonSecondaire>}
        <Link
          to={demande.lien.url}
          className="inline-flex min-h-[44px] items-center whitespace-nowrap rounded-xl px-3 text-sm font-semibold text-codialis-sky hover:underline"
        >
          {demande.lien.libelle}
        </Link>
      </div>
    </article>
  );
}

function ModaleRefus({ demande, onFermer }: { demande: VueDemande; onFermer: () => void }): JSX.Element {
  const refuser = useRefuserDemande();
  const toast = useToast();
  const [motif, setMotif] = useState('');
  const valide = motif.trim().length >= MOTIF_MIN;

  return (
    <Modale
      titre="Refuser la demande"
      sousTitre={`${demande.typeLibelle} · ${demande.objet}`}
      ouverte
      onFermer={onFermer}
    >
      <form
        className="space-y-3 p-4 sm:p-6"
        onSubmit={(evenement) => {
          evenement.preventDefault();
          if (!valide) return;
          refuser.mutate(
            { id: demande.id, motif: motif.trim() },
            {
              onSuccess: (resultat) => {
                toast(resultat.message);
                onFermer();
              },
            },
          );
        }}
      >
        <p className="text-sm text-codialis-gray">
          Rien n’est modifié. {demande.demandePar ?? 'L’auteur de la demande'} reçoit le motif dans l’application et par
          e-mail.
        </p>
        <label htmlFor="motif-refus" className="block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
          Motif du refus
        </label>
        <textarea
          id="motif-refus"
          required
          rows={4}
          maxLength={MOTIF_MAX}
          value={motif}
          onChange={(evenement) => setMotif(evenement.target.value)}
          className="w-full min-w-0 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-codialis-navy"
        />
        {refuser.error && (
          <MessageErreur message={messageErreur(refuser.error, 'La demande n’a pas pu être refusée.')} />
        )}
        <div className="flex flex-wrap justify-end gap-2">
          <BoutonSecondaire onClick={onFermer}>Annuler</BoutonSecondaire>
          <BoutonPrincipal type="submit" disabled={!valide || refuser.isPending}>
            {refuser.isPending ? 'Refus…' : 'Refuser la demande'}
          </BoutonPrincipal>
        </div>
      </form>
    </Modale>
  );
}
